import React, { useMemo } from 'react';
import { Box, Button, Center, Flex, Menu, MenuButton, MenuList, Text } from '@chakra-ui/react';
import useNavigation from '../../../hooks/useNavigation';
import styled from '@emotion/styled/macro';
import { Masonry } from 'components/masonry/Masonry';
import StyledStandaloneIconWrapper from '../StyledStandaloneIconWrapper';
import Tooltip from '../../utils/Tooltip';

const menuVisualSettings = {
    itemHorizontalSpacing: 12,
    itemVerticalSpacing: 16,
    itemWidth: 261,
    wrapperSpacing: 10,
    padding: 10,
};

const NavigationMenu = ({ config, renderItem }) => {
    const { assetsMap } = useNavigation();

    const menuType = useMemo(
        () => (config?.items?.length && config.items[0].type === 'MenuItem' ? 'MenuOfLinks' : 'MenuOfGroups'),
        [config?.items]
    );

    const needsMasonry = useMemo(() => {
        return config?.items?.some((item) => item?.type === 'MenuItemsGroup');
    }, [config?.items]);

    const { columnsCount, columnDistribution } = useMemo(() => {
        const MAX_ITEMS_PER_COLUMN = 15; // Max number of MenuItemsGroups + MenuItems per column

        if (menuType === 'MenuOfGroups') {
            const items = config?.items || [];

            // Helper function to calculate total items in a MenuItemsGroup
            const getGroupItemCount = (group) => {
                return 1 + (group?.items?.length ?? 0); // 1 for the group header + its items
            };

            let columnCount = 1;
            let currentColumnItems = 0;
            const distribution = [];

            // Process MenuItemsGroups sequentially
            for (const [index, item] of items.entries()) {
                const itemCount = getGroupItemCount(item);

                // Check if adding this group would exceed the max items per column
                if (currentColumnItems > 0 && currentColumnItems + itemCount > MAX_ITEMS_PER_COLUMN) {
                    // Move to next column
                    columnCount++;
                    currentColumnItems = itemCount;
                    distribution[index] = columnCount - 1; // 0-based index
                } else {
                    // Add to current column
                    currentColumnItems += itemCount;
                    distribution[index] = columnCount - 1; // 0-based index
                }
            }
            return { columnsCount: columnCount, columnDistribution: distribution };
        }

        return { columnsCount: 1, columnDistribution: null };
    }, [config?.items, menuType]);

    const wrapperWidth = useMemo(() => {
        return (
            menuVisualSettings.itemWidth * columnsCount +
            menuVisualSettings.itemHorizontalSpacing * (columnsCount > 0 ? columnsCount - 1 : 0) +
            menuVisualSettings.wrapperSpacing * 2
        );
    }, [columnsCount]);

    const menuButton = (
        <StyledMenuButton as={Button} variant="ghost">
            {config.label ? (
                <Flex alignItems={'center'} borderRadius={6}>
                    {config.icon}

                    <StyledText
                        withGap={!config.icon}
                        labelMaxWidth={config?.desktopConfig?.labelMaxWidth}
                        noOfLines={1}
                        wordBreak={'break-all'}
                    >
                        {config.label}
                    </StyledText>

                    {assetsMap?.dropdownArrow}
                </Flex>
            ) : (
                <StyledStandaloneIconWrapper data-comp="standalone-icon-wrapper">
                    {config.icon}
                </StyledStandaloneIconWrapper>
            )}
        </StyledMenuButton>
    );

    return (
        <Menu placement={config?.desktopConfig?.menuPlacement ?? 'bottom'}>
            {config?.desktopConfig?.tooltipLabel && (!config.label || config.label === '') ? (
                <Tooltip label={config.desktopConfig.tooltipLabel}>{menuButton}</Tooltip>
            ) : (
                menuButton
            )}

            <StyledMenuList
                position={'relative'}
                maxWidth={wrapperWidth}
                padding={`${menuVisualSettings.wrapperSpacing}px`}
                marginBottom={`-${menuVisualSettings.itemVerticalSpacing}px`}
                wrapperWidth={wrapperWidth}
                bg="background-tertiary"
            >
                {needsMasonry ? (
                    <Masonry
                        p={`${menuVisualSettings.padding}px`}
                        columns={columnsCount}
                        gap={menuVisualSettings.itemHorizontalSpacing}
                        customDistribution={columnDistribution}
                    >
                        {config.items.map((menuItem, menuItemIndex) => (
                            <StyledMasonryItem key={menuItemIndex}>{renderItem(menuItem)}</StyledMasonryItem>
                        ))}
                    </Masonry>
                ) : (
                    <Center
                        width="full"
                        p={`${menuVisualSettings.padding}px`}
                        flexDir={'column'}
                        bg="background-tertiary"
                    >
                        {config.items.map((menuItem, menuItemIndex) => (
                            <React.Fragment key={menuItemIndex}>{renderItem(menuItem)}</React.Fragment>
                        ))}
                    </Center>
                )}
            </StyledMenuList>
        </Menu>
    );
};

const StyledMenuButton = styled(MenuButton)`
    padding: 0;
    min-width: 0;
    min-height: 0;
    background-color: var(--chakra-menu-item-background);

    &[data-active],
    &:hover {
        [data-comp='navigation-asset'] {
            color: var(--chakra-colors-blue-400);
        }
    }

    &[data-active] {
        [data-comp='standalone-icon-wrapper'] {
            background-color: var(--chakra-colors-blue-100);
        }
    }
`;

const StyledText = styled(Text, { shouldForwardProp: (prop) => !['withGap', 'labelMaxWidth'].includes(prop) })`
    color: var(--chakra-colors-gray-800);
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;
    margin-left: ${(props) => (props.withGap ? 'var(--chakra-space-2)' : 0)};
    max-width: ${(props) => (props.labelMaxWidth ? `${props.labelMaxWidth}px` : 'auto')};
`;

const StyledMenuList = styled(MenuList, { shouldForwardProp: (prop) => prop !== 'wrapperWidth' })`
    box-sizing: content-box;
    padding: 0;
    position: relative;
    width: ${(props) => props.wrapperWidth}px;
`;

const StyledMasonryItem = styled(Box)`
    width: ${menuVisualSettings.itemWidth}px;
    margin-bottom: ${menuVisualSettings.itemVerticalSpacing}px;
    background-color: var(--chakra-colors-background-tertiary);
`;

export default NavigationMenu;
